import { FocusEventHandler, FunctionComponent } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { IconButton, IconButtonProps } from '@mui/material';
import { DatePicker, DatePickerProps, DatePickerToolbar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { Field } from '../../lib/recoil/Form';

import { format, isValid, parseISO, startOfMonth } from 'date-fns';

interface MonthPickerProps extends Partial<DatePickerProps<Date>>{
  id?: string;
  field: Field<string>;
  label: string;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: (el: unknown) => void;
  outputDateFormat?: string;
}

/**
 * [MUI DatePicker](https://mui.com/components/date-picker/)
 *  for selecting year + month in a single view
 */
const MonthPicker: FunctionComponent<MonthPickerProps> = ({
  id,
  field,
  label,
  onBlur = () => {},
  onChange = () => {},
  outputDateFormat,
  ...props
}) => {
  const [ value, setValue ] = useRecoilState(field.valueState);
  const error = useRecoilValue(field.errorState);
  const helperText = useRecoilValue(field.helperTextState);
  const [ touched, setTouched ] = useRecoilState(field.touchedState);

  const valueAsDate = value ? parseISO(value) : null;

  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    setTouched(true);
    onBlur(event);
  };

  const handleChange = (newDate: Date | null) => {
    setTouched(true);
    onChange(newDate);

    if (newDate && isValid(newDate)) {
      if (outputDateFormat) {
        setValue(format(startOfMonth(newDate), outputDateFormat ));
      } else {
        setValue(startOfMonth(newDate).toISOString());
      }
    } else {
      setValue('');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker<Date>
        disablePast
        format='MM/yyyy'
        label={label}
        minDate={startOfMonth(new Date())}
        onChange={handleChange}
        openTo='month'
        slotProps={
          {
            field: {
              id,
            },
            popper: {
              sx: {
                '.MuiDateCalendar-root': { height: '30vh' },
              },
            },
            textField: {
              error: touched && error,
              fullWidth: true,
              helperText: helperText,
              id: 'date-picker-input',
              inputProps: {
                placeholder: format(new Date(), 'MM/yyyy'),
              },
              onBlur: handleBlur,
            },
          }
        }
        slots={{ openPickerButton: CustomOpenPickerButton, toolbar: DatePickerToolbar }}
        value={valueAsDate}
        views={[ 'year', 'month' ]}
        {...props}
      />
    </LocalizationProvider>
  );
};

// eslint-disable-next-line react/no-multi-comp
const CustomOpenPickerButton: React.FC<IconButtonProps> = (props) => (
  <IconButton {...props} data-cy='date-picker-input'>
    {props.children}
  </IconButton>
);

export default MonthPicker;
